import type { FC } from 'react';
import { useState } from 'react';
import { Popper } from '@mui/base';
import { Autocomplete, TextField } from '@mui/material';

import { useDebounce } from 'src/hooks';
import type { DetailedFollowUpTicket, FollowUpOwner } from 'src/types/followUp';
import useTicketDetailQuery from 'src/components/followUp/hooks/useTicketDetailQuery';

import useTicketOwnerUpdate from './useTicketOwnerUpdate';
import useTicketPotentialOwnersQuery from './useTicketPotentialOwnersQuery';

interface ChangeOwnerTicketDetailModalProps {
  onClose: () => void;
  anchorEl: HTMLElement | null;
  ticket: DetailedFollowUpTicket;
  refetchList: () => void;
}

const ChangeOwnerTicketDetailModal: FC<ChangeOwnerTicketDetailModalProps> = ({
  onClose,
  anchorEl,
  ticket,
  refetchList,
}) => {
  const [searchString, setSearchString] = useState<string>('');
  const debouncedOwnerSearch = useDebounce(searchString, 500);
  const { refetch } = useTicketDetailQuery(ticket.id);
  const { data } = useTicketPotentialOwnersQuery({
    search: debouncedOwnerSearch,
    ticketId: ticket?.id,
  });
  const potentialOwners = data?.results || [];
  const { mutateAsync } = useTicketOwnerUpdate({
    ticketId: ticket?.id,
  });

  const onChange = (_: unknown, value: FollowUpOwner | null) => {
    mutateAsync({ ownerId: value?.id }).then(() => {
      void refetch();
      void refetchList();
    });
    onClose();
  };

  return (
    <Popper
      id={'dropdownId'}
      anchorEl={anchorEl}
      placement="bottom-end"
      style={{
        width: '100%',
        padding: '0.25rem 1rem',
      }}
      open={!!anchorEl}
      disablePortal
    >
      {potentialOwners && (
        <Autocomplete
          renderInput={(params) => <TextField {...params} />}
          getOptionLabel={(option) => option.fullName}
          options={potentialOwners}
          inputValue={searchString}
          onInputChange={(_, newInputValue) => {
            setSearchString(newInputValue);
          }}
          open={true}
          onChange={onChange}
          ListboxProps={{ style: { maxHeight: '12rem' } }}
        />
      )}
    </Popper>
  );
};

export default ChangeOwnerTicketDetailModal;
